import React, { useState, useCallback, FC } from "react";
import { Flex } from "@chakra-ui/react";
import clsx from "clsx";
import Tooltip from "rc-tooltip";
import url from "url";
import SafeImage from "@/js/components/SafeImage";
import { CloseButton } from "@/js/components/shared/CloseButton";
import WebsiteOrSignatureListModal from "@/js/components/WebsiteOrSignatureListModal";
import useStore from "@/stores";
import { trimString, platformIcon, WindowData, BackgroundTab, isCompanionMode } from "../../RegularClassroomUser/utils";
import { RestrictedAppsModal } from "../../RegularClassroomUser/components/RestrictedAppsModal";
import { IGetSignatureDbReturn } from "@/stores/signatureStore_I";
import { User } from "@/js/utils/userUtils";
import { ChakraFAIcon } from "@/components/ui/Icon";
import { CompanionIcon } from "@/components/ui/custom-icons/CompanionIcon";

interface StudentTileScreenshotProps {
	user: User;
	windowData: WindowData;
	isScreenshotLoading: boolean;
	isMonitorMode: boolean;
	forceCloseTab: (tabId: string, force: boolean) => void;
	classIsActive: boolean;
	canCloseTab: boolean;
	signatureDb: IGetSignatureDbReturn;
	openP2pModal?: (
		user: User,
		tabId: string | undefined,
		chromeId: string | undefined,
		windowId: string | undefined,
		isActiveTab: boolean
	) => void;
	tabItemLimit: number;
	/**
	 * Is the teacher currently restricted from seeing the screen based on the domain or app in view? (via Screen Visibility Restrictions)
	 */
	isRestricted: boolean;
	/**
	 * Is the student currently viewing a screenshare from the teacher?
	 */
	inScreenshare: boolean;
}

const StudentTileScreenshot: FC<StudentTileScreenshotProps> = (props: StudentTileScreenshotProps) => {
	const [listModalOpen, setListModalOpen] = useState(false);

	const openP2pModal = useCallback(
		(tabId: string | undefined, isActiveTab: boolean = false) => {
			if (props.isRestricted) {
				return;
			}

			if (props.openP2pModal) {
				props.openP2pModal(props.user, tabId, props.windowData.chrome_id, props.windowData.chrome_window_id, isActiveTab);
				return;
			}
		},
		[props]
	);

	const openListModal = () => {
		setListModalOpen(true);
	};

	const closeListModal = () => {
		setListModalOpen(false);
	};

	const renderTabs = () => {
		let tabs: JSX.Element[] = [];
		if (props.windowData.background_tabs) {
			tabs = props.windowData.background_tabs.map((tab: BackgroundTab, i: number) => (
				<SafeImage
					key={tab.tabUrl + i}
					styleClassName={clsx(props.isRestricted ? "tab-favicon--noInteract" : "tab-favicon clickable")}
					title={tab.title}
					src={tab.favIcon}
					alt={""}
					onClick={() => openP2pModal(tab.tab_id, false)}
				/>
			));
		}
		if (tabs.length > props.tabItemLimit) {
			const ret = tabs.slice(0, props.tabItemLimit);
			const additionalTabs = Math.min(tabs.length - props.tabItemLimit, 99);
			const c = clsx("tab-favicon tab-favicon--hidden-tabs clickable", additionalTabs > 9 && "tab-favicon--small-text");
			ret.push(
				<SafeImage
					key="hidden-tabs-icon"
					styleClassName={c}
					title={tabs.length - props.tabItemLimit + " hidden items"}
					src={""}
					alt={"+" + String(additionalTabs)}
					onClick={openListModal}
				/>
			);
			return ret;
		} else {
			return tabs;
		}
	};

	const disableCloseTab = () => {
		return props.isScreenshotLoading || !props.windowData.tab_id || !props.classIsActive;
	};

	const renderScreen = () => {
		if (props.isScreenshotLoading) {
			return <div className="RegularClassroomUser_screenshotLoading">Loading new screenshot...</div>;
		} else {
			return props.windowData.url !== "" || useStore.getState().featureToggles.data["agent-p2p-screenshots"] ? (
				<div
					className={clsx("RegularClassroomUser_screenshotContainer", !props.isRestricted && "clickable")}
					onClick={() => openP2pModal(props.windowData.tab_id, true)}
				>
					<img
						className="RegularClassroomUser_screenshotImage"
						alt={trimString(props.windowData.title, 40)}
						src={props.windowData.screenshot}
					/>
				</div>
			) : (
				<div className="RegularClassroomUser_screenshotContainer">
					<img
						className="RegularClassroomUser_screenshotImage"
						alt={trimString(props.windowData.title, 40)}
						src={props.windowData.screenshot}
					/>
				</div>
			);
		}
	};

	return (
		<>
			<div className="RegularClassroomUser_screenHeader">
				{props.inScreenshare && (
					<Flex mr={4} width={"24px"} flexShrink={0}>
						<ChakraFAIcon icon="fa-screencast" variant="solid" boxSize={5} color={"#0075DB"} />
					</Flex>
				)}
				{props.windowData.url !== "" || useStore.getState().featureToggles.data["agent-p2p-screenshots"] ? (
					props.isRestricted ? (
						<RestrictedAppsModal
							screenshotData={props.windowData}
							isMonitorMode={props.isMonitorMode}
							isClassActive={props.classIsActive}
							forceCloseTab={props.forceCloseTab}
							canCloseTab={props.canCloseTab}
							signatures={props.signatureDb}
							student={props.user}
						/>
					) : (
						<Tooltip
							placement="bottomLeft"
							trigger={url.parse(props.windowData.url).protocol ? ["hover"] : []}
							destroyTooltipOnHide={true}
							overlayClassName={"Tooltip_OpenNewTabHyperlink"}
							overlay={
								<a href={props.windowData.url} target="_blank" rel="noopener noreferrer">
									Open in new tab
								</a>
							}
						>
							<span
								className={clsx("RegularClassroomUser_screenHeaderText", !props.isRestricted && "clickable")}
								title={props.windowData.title}
								data-testid="button-live-view-modal"
								onClick={() => openP2pModal(props.windowData.tab_id, true)}
							>
								{!props.isScreenshotLoading && props.windowData.title}
							</span>
						</Tooltip>
					)
				) : (
					<span className={"RegularClassroomUser_screenHeaderText"} title={props.windowData.title}>
						{!props.isScreenshotLoading && props.windowData.title}
					</span>
				)}
				{props.windowData.url !== "" && !props.isRestricted && (
					<Flex marginLeft={"auto"}>
						{props.canCloseTab && (
							<CloseButton
								tooltipText="Close Tab"
								tooltipId={props.windowData.tab_id + "_closeTabTooltip"}
								disabled={disableCloseTab()}
								onClick={() => {
									props.forceCloseTab(props.windowData.tab_id, true);
								}}
							/>
						)}
					</Flex>
				)}
			</div>
			{renderScreen()}
			<WebsiteOrSignatureListModal
				title={"Open Tabs"}
				open={listModalOpen}
				activeTab={props.windowData}
				items={props.windowData.background_tabs}
				signatureDb={props.signatureDb}
				user={props.user}
				onClose={closeListModal}
				forceCloseTab={props.forceCloseTab}
				canCloseTab={props.canCloseTab}
				classIsActive={props.classIsActive}
				isMonitorMode={props.isMonitorMode}
			/>
			<div className="RegularClassroomUser_tabRows">
				{props.windowData.background_tabs && props.windowData.background_tabs.length > 0 && (
					<div className="RegularClassroomUser_tabRow">
						<button data-testid="button-open-info-modal" className="RegularClassroomUser_tabsAgentIcon" onClick={openListModal}>
							{isCompanionMode(props.windowData) ? (
								<CompanionIcon color="inherit" boxSize={4} />
							) : (
								<ChakraFAIcon
									icon={platformIcon(props.windowData).iconName}
									variant={platformIcon(props.windowData).variant}
									color="inherit"
									boxSize={4}
								/>
							)}
						</button>
						{renderTabs()}
					</div>
				)}
			</div>
		</>
	);
};

export default StudentTileScreenshot;
